import { z } from 'zod';

/** What a driver can be told (gitaumoses4/chukua#61). `office_message` is sent when the office writes on a trip's message thread (#106). */
export const NOTIFICATION_TYPES = ['trip_assigned', 'trip_changed', 'office_message', 'document_expiring'] as const;
export type NotificationType = (typeof NOTIFICATION_TYPES)[number];

export const NOTIFICATION_CHANNELS = ['push', 'sms'] as const;
export type NotificationChannel = (typeof NOTIFICATION_CHANNELS)[number];

/** A driver who turns both channels off for these would miss the work itself, so the server refuses it. */
export const MUST_REACH_NOTIFICATION_TYPES: readonly NotificationType[] = ['trip_assigned', 'trip_changed'];

/** `PUT /sync/push-token`: the FCM registration token of this phone. The platform is already on the device row from sign-in. */
export const syncPushTokenRequestSchema = z.object({ token: z.string().trim().min(1).max(4096) }).strict();
export type SyncPushTokenRequest = z.infer<typeof syncPushTokenRequestSchema>;

const preferenceChangeSchema = z.object({
  type: z.enum(NOTIFICATION_TYPES),
  channel: z.enum(NOTIFICATION_CHANNELS),
  enabled: z.boolean(),
});

/** `PUT /sync/notification-preferences`: only the toggles the driver changed; anything not named keeps its setting. */
export const syncNotificationPreferencesRequestSchema = z
  .object({
    changes: z
      .array(preferenceChangeSchema)
      .min(1)
      .max(NOTIFICATION_TYPES.length * NOTIFICATION_CHANNELS.length),
  })
  .strict();
export type SyncNotificationPreferencesRequest = z.infer<typeof syncNotificationPreferencesRequestSchema>;

/** One toggle. Every type and channel is listed, because "no row" means on and the app should not have to know that. */
export interface NotificationPreferenceView {
  type: NotificationType;
  channel: NotificationChannel;
  enabled: boolean;
}

export interface NotificationPreferencesView {
  items: NotificationPreferenceView[];
}

/** How long a phone may go without calling the server before a notification goes by SMS instead of push; set on the company page. */
export const NOTIFICATION_SMS_FALLBACK_MINUTES_RANGE = { min: 5, max: 1440 } as const;
/** A placeholder for Index Cargo to confirm: it must be longer than the app's 15-minute background sync plus Doze slack. */
export const DEFAULT_NOTIFICATION_SMS_FALLBACK_MINUTES = 60;
